import React, { useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useAsyncAction } from 'modus/components/form/async-actions/AsyncAction';
import { catchError } from 'rxjs/operators';
import _ from 'lodash';
import { Box, CircularProgress, Typography } from '@material-ui/core';
import AuthorizerService from '../service/AuthorizerService';
import { ResourceType, AuthorizationRequest, AuthorizationResponse } from '../types';
import { SET_AUTHORIZATIONS } from 'store/reducers/authorizationManager';
import { useCurrentTeam } from 'libs/security/authorization/Permission';

interface AuthorizationProviderProps {
  children: React.ReactNode;
  resources: AuthorizationRequest[];
  resourceType: ResourceType;
}

// Hook to get authorization status for a specific resource
export const useAuthorization = (resourceType: ResourceType, resourceId: string) => {
  const resourcePermissions = useSelector((state: any) => 
    state?.authorizationManager?.[resourceType]?.[resourceId]
  );

  return useMemo(() => ({
    isActionAllowed: (action: string) => resourcePermissions?.[action] === true,
    permissions: resourcePermissions || {},
  }), [resourcePermissions]);
};

export const AuthorizationProvider: React.FC<AuthorizationProviderProps> = ({ 
  children, 
  resources,
  resourceType 
}) => {
  const dispatch = useDispatch();
  const { teamId: storedTeamId } = useCurrentTeam();
  const teamId = useSelector(
    (state: any) =>
      state?.teamManager?.teams?.currentTeam?.id ??
      storedTeamId
  );
  
  // State will be available from redux store for components to consume

  const { state: asyncState, subscribe: fetchAuthorizations } = useAsyncAction<AuthorizationResponse[]>(() => {
    console.log('Authorization attempt with:', { resources, teamId });
    return AuthorizerService.authorize(resources, teamId)
      .pipe(
        catchError(error => {
          console.error('Authorization API error:', error);
          throw error;
        })
      );
  });

  // Check if we already have authorizations for this resource type
  const existingAuthorizations = useSelector((state: any) => 
    state?.authorizationManager?.[resourceType]
  );

  useEffect(() => {
    console.log('AuthorizationProvider effect triggered:', { teamId, resourcesLength: resources.length });
    
    if (!teamId || resources.length === 0) {
      return;
    }

    // Check if all requested resources already exist in state
    const allResourcesExist = resources.every(resource => 
      existingAuthorizations && existingAuthorizations[resource.resourceId]
    );

    if (allResourcesExist) {
      console.log('All resources already authorized:', {
        resourceType,
        resources: resources.map(r => r.resourceId),
        existingAuths: existingAuthorizations
      });
      return;
    }

    console.log('Fetching authorizations for missing resources');
    const unsubscribe = fetchAuthorizations();
    return () => unsubscribe();
  }, [teamId, resources, fetchAuthorizations, existingAuthorizations, resourceType]);



  // Only update redux when we have new authorization data
  useEffect(() => {
    if (!asyncState.loaded || !asyncState.value) return;

    // Compare new values with existing ones to prevent unnecessary updates
    const hasChanges = resources.some(resource => {
      const newAuth = asyncState.value?.find(auth => auth.resourceId === resource.resourceId);
      if (!newAuth) return false;

      const existingAuth = existingAuthorizations?.[resource.resourceId];
      
      // If we don't have existing auth, or if the allowed actions have changed
      return !existingAuth || 
        !_.isEqual(
          new Set(newAuth.allowedActions), 
          new Set(Object.keys(existingAuth).filter(key => existingAuth[key]))
        );
    });

    if (hasChanges) {
      console.log('Authorization changes detected, updating state:', {
        resourceType,
        resources: resources.map(r => r.resourceId)
      });
      
      dispatch({
        type: SET_AUTHORIZATIONS,
        payload: asyncState.value,
        meta: { resourceType }
      });
    } else {
      console.log('No authorization changes detected, skipping update');
    }
  }, [asyncState.loaded, asyncState.value, dispatch, existingAuthorizations, resourceType, resources]);

  return (
    <>
      {asyncState.loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <Box textAlign="center">
            <CircularProgress size={24} style={{ marginBottom: '8px' }} />
            <Typography variant="body2" color="textSecondary">
              Verifying authorization...
            </Typography>
          </Box>
        </Box>
      ) : asyncState.error ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <Box textAlign="center">
            <Typography variant="body2" color="error">
              Error verifying authorization. Please try again.
            </Typography>
          </Box>
        </Box>
      ) : (
        children
      )}
    </>
  );
};

